/**
 * Whether the "Edit Quick Estimate" and "Retry Estimate" actions are offered for a client in the
 * clients list.
 *
 * The usual ways into quick edit ("Update Intake Form", "Intake Form Data" → Edit) need a
 * questionnaire link, which Quick Plan clients never have, and they sit behind the `intake-form`
 * pilot restriction, which Edward Jones has. So an EDJ advisor had no way to fix a mistyped DOB, ZIP
 * or net worth on a quick estimate. This action opens the limited EDJ quick-edit form directly.
 *
 * Both conditions must hold:
 * - the advisor has the EDJ Quick Plan (`useQuickPlanAvailability().isEdjQuickPlan`);
 * - the client was created by the EDJ Quick Plan (`isEdjQuickPlanClient`, its `metadata.quickPlan`
 *   marker). The marker does not depend on a prediction existing, so a client whose first model run
 *   failed ("Algorithms error") can still be corrected or retried. EDJ clients stay quick estimates
 *   for the pilot (they are never sent the full questionnaire), so the marker is enough.
 */
import { isEdjQuickPlanClient } from '@shared';
import { ALGORITHMS_ERROR } from '@client/util/constants';
import { useQuickPlanAvailability } from './use-quick-plan-availability';

export function useCanEditEdjQuickEstimate(client: Pick<Client, 'metadata'>): boolean {
  const { isEdjQuickPlan } = useQuickPlanAvailability();
  return isEdjQuickPlan && isEdjQuickPlanClient(client);
}

/** "Retry Estimate": the same client, when its model run failed ("Algorithms error"). */
export function useCanRetryEdjQuickEstimate(client: Pick<Client, 'metadata' | 'clientTags'>): boolean {
  const canEdit = useCanEditEdjQuickEstimate(client);
  return canEdit && !!client.clientTags?.includes(ALGORITHMS_ERROR);
}
